import { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Controller } from "react-hook-form";
import {
    Breadcrumb,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    Row,
    Select,
    Space,
    Tag,
    Typography,
} from "antd";
import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    CloseOutlined,
    IdcardOutlined,
    SafetyCertificateOutlined,
    SolutionOutlined,
} from "@ant-design/icons";

import { IRole } from "../../../../interfaces";
import useRoleForm from "./useRoleForm";
import PageHeader from "../../../../components/PageHeader";
import { ThemeContext } from "../../../../contexts/ThemeContext";

const { Text } = Typography;

interface IProps {
    action: "Adicionar" | "Editar";
    role?: IRole;
    onSubmit: (role: IRole) => Promise<void>;
}

export default function RoleForm({ action, role, onSubmit }: IProps) {
    const {
        isLoadingPermissions,
        permissions,
        handleSubmitForm,
        handleSubmit,
        isSubmiting,
        errors,
        control,
        watchedPermissionIds,
        handleSelectAllPermissions,
        handleClearAllPermissions,
    } = useRoleForm(onSubmit, role);

    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const navigate = useNavigate();

    const isEdit = action === "Editar";

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* External Breadcrumb Card */}
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}`,
                    padding: "10px 16px",
                    borderRadius: 8,
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to="/configuracoes/perfis">Perfis de Acesso</Link>,
                        },
                        {
                            title: isEdit ? `Editar Perfil #${role?.id ?? "--"}` : "Novo Perfil",
                        },
                    ]}
                />
            </div>

            {/* Page Header */}
            <PageHeader
                icon={<SafetyCertificateOutlined />}
                iconBg="linear-gradient(135deg, #722ed1 0%, #1677ff 100%)"
                title={isEdit ? `Editar Perfil: ${role?.name || ""}` : "Novo Perfil de Acesso"}
                tag={isEdit && role?.id ? <Tag color="purple">ID #{role.id}</Tag> : undefined}
                subtitle={
                    isEdit
                        ? "Atualize o nome, a descrição e o conjunto de permissões de acesso deste perfil"
                        : "Cadastre um novo perfil e defina quais funcionalidades e módulos ele poderá acessar"
                }
                actions={
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate("/configuracoes/perfis")}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            fontWeight: 500,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Voltar para Perfis
                    </Button>
                }
            />

            {/* Form Container Card */}
            <Card
                style={{
                    borderRadius: 12,
                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                    background: isDark ? "#141414" : "#ffffff",
                    boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.03)",
                }}
                bodyStyle={{ padding: "20px 24px" }}
            >
                <Form
                    onFinish={handleSubmit(handleSubmitForm)}
                    autoComplete="off"
                    layout="vertical"
                >
                    {/* Section 1: Identificação do Perfil */}
                    <div style={{ marginBottom: 20 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                            }}
                        >
                            <IdcardOutlined style={{ color: "#722ed1", fontSize: 18 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                    Identificação do Perfil
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Nome e resumo do escopo de atuação deste perfil
                                </Text>
                            </div>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<strong>Nome do Perfil</strong>}
                                    required
                                    validateStatus={errors.name ? "error" : undefined}
                                    help={errors.name?.message}
                                >
                                    <Controller
                                        name="name"
                                        control={control}
                                        rules={{
                                            required: "Por favor, digite o nome do perfil!",
                                            minLength: {
                                                value: 2,
                                                message: "O nome deve ter pelo menos 2 caracteres.",
                                            },
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Ex: Administrador Geral, Operador de Atendimento, Auditor"
                                                size="large"
                                                style={{ borderRadius: 8 }}
                                                prefix={<IdcardOutlined style={{ color: "#722ed1" }} />}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<strong>Descrição do Perfil</strong>}
                                    required
                                    validateStatus={errors.description ? "error" : undefined}
                                    help={errors.description?.message}
                                >
                                    <Controller
                                        name="description"
                                        control={control}
                                        rules={{
                                            required: "Por favor, digite a descrição do perfil!",
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                {...field}
                                                placeholder="Ex: Acesso total a todas as rotas e módulos do painel administrativo"
                                                size="large"
                                                style={{ borderRadius: 8 }}
                                                prefix={<SolutionOutlined style={{ color: "#1677ff" }} />}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    {/* Section 2: Permissões de Acesso */}
                    <div style={{ marginBottom: 24 }}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: 10,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#262626" : "#eaedf1"}`,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <SafetyCertificateOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                                <div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                                            Permissões de Acesso
                                        </Text>
                                        <Tag color="purple" style={{ borderRadius: 10, margin: 0, fontWeight: 600, fontSize: 11 }}>
                                            {watchedPermissionIds.length} de {permissions.length} selecionada{watchedPermissionIds.length === 1 ? "" : "s"}
                                        </Tag>
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 11.5 }}>
                                        Selecione as permissões e funcionalidades que os usuários deste perfil poderão executar
                                    </Text>
                                </div>
                            </div>

                            <Space size={8}>
                                <Button
                                    size="small"
                                    type="dashed"
                                    icon={<CheckCircleOutlined />}
                                    onClick={handleSelectAllPermissions}
                                    disabled={isLoadingPermissions || watchedPermissionIds.length === permissions.length}
                                    style={{ borderRadius: 6, fontSize: 12 }}
                                >
                                    Selecionar Todas
                                </Button>
                                <Button
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={handleClearAllPermissions}
                                    disabled={isLoadingPermissions || watchedPermissionIds.length === 0}
                                    style={{ borderRadius: 6, fontSize: 12 }}
                                >
                                    Limpar Seleção
                                </Button>
                            </Space>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item
                                    label={<strong>Permissões Atribuídas</strong>}
                                    required
                                    validateStatus={errors.permission_ids ? "error" : undefined}
                                    help={errors.permission_ids?.message}
                                >
                                    <Controller
                                        name="permission_ids"
                                        control={control}
                                        rules={{
                                            required: "Por favor, selecione ao menos uma permissão para este perfil!",
                                        }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                mode="multiple"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={permissions.map((p) => ({
                                                    value: p.id,
                                                    label: p.description || p.name,
                                                }))}
                                                placeholder="Clique para pesquisar e selecionar as permissões..."
                                                disabled={isLoadingPermissions}
                                                loading={isLoadingPermissions}
                                                size="large"
                                                style={{ width: "100%", borderRadius: 8 }}
                                            />
                                        )}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <Divider style={{ margin: "20px 0 16px 0" }} />

                    {/* Section 3: Rodapé de Ações */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
                        <Button
                            onClick={() => navigate("/configuracoes/perfis")}
                            disabled={isSubmiting}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 500,
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<CheckOutlined />}
                            loading={isSubmiting}
                            disabled={isSubmiting || isLoadingPermissions}
                            style={{
                                height: 38,
                                borderRadius: 8,
                                fontWeight: 600,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                            }}
                        >
                            {isEdit ? "Salvar Alterações" : "Cadastrar Perfil"}
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
}
